/**
 * Lopende Trajecten & Regie — API-client (4 strikt gescheiden datatables).
 * Backend: src/routes/lopendeTrajecten.ts
 */
import api from './api';

const d = <T,>(r: { data: { data: T } }) => r.data.data;
const BASE = '/employer/lopende-trajecten';

// ─── Overzicht ────────────────────────────────────────────────────────────────

export interface TrajectenOverzicht {
  preventief: number;
  poortwachter_open: number;
  b2b_te_fiatteren: number;
  calamiteiten_actief: number;
}
export const getTrajectenOverzicht = () => api.get<{ data: TrajectenOverzicht }>(`${BASE}/overzicht`).then(d);

// ─── Tabel 1: Preventief & anoniem verbruik ───────────────────────────────────

export interface PreventiefRegel {
  bron: 'sessie' | 'academy';
  pseudoniem: string;
  discipline_label: string;
  professional_type: string;
  sessies: number;
  uren_verbruikt: number | null;
  reistijd_uren: number;
  transactie_status: 'nog_niet_verbruikt' | 'open' | 'gefactureerd' | 'betaald' | 'wacht_op_stripe' | string;
  laatste_activiteit: string | null;
  bedrag: number | null;
  referentie_id: string;
}
export interface PreventiefVerbruik {
  regels: PreventiefRegel[];
  totaal: { trajecten: number; academy_boekingen: number; uren: number; academy_bedrag: number; unieke_pseudoniemen: number };
}
export const getPreventiefVerbruik = () => api.get<{ data: PreventiefVerbruik }>(`${BASE}/preventief`).then(d);

// ─── Tabel 2: Wet Verbetering Poortwachter ────────────────────────────────────

export type PoortwachterStatus =
  | 'CASE_OPENED'
  | 'PROBLEM_ANALYSIS_DUE'
  | 'PLAN_OF_ACTION_ACTIVE'
  | 'FIRST_YEAR_EVALUATION'
  | 'WIA_APPLICATION'
  | 'CLOSED';

export type PoortwachterMijlpaalKey =
  | 'ziekmelding'
  | 'probleemanalyse'
  | 'plan_van_aanpak'
  | 'periodieke_evaluatie'
  | 'uwv_42e_week'
  | 'eerstejaarsevaluatie'
  | 'wia_aanvraag'
  | 'einde_loondoorbetaling';

/** Eén mijlpaal uit de tijdlijn; datums berekent de database-trigger bij de ziekmelding. */
export interface PoortwachterDeadline {
  id: string;
  key: PoortwachterMijlpaalKey;
  volgnummer: number;
  week: number;
  label: string;
  rol: 'bedrijfsarts' | 'arbeidsdeskundige' | null;
  datum: string;
  dagen_te_gaan: number;
  signaal: string | null;
  signaal_actief: boolean;
  status: 'OPEN' | 'AFGEROND' | 'VERVALLEN';
  urgentie: 'afgerond' | 'verlopen' | 'urgent' | 'ok';
  afgerond_op: string | null;
  handmatig: boolean;
  checklist: Array<{ key: string; label: string; gedaan: boolean }> | null;
}

/** Kolom 4: de 8 WVP-stappen van de statemachine. */
export type WvpStap =
  | 'ZIEKMELDING_PENDING'
  | 'PROBLEMANALYSE_W6'
  | 'PLAN_VAN_AANPAK_W8'
  | 'CONTACTMOMENT_6W'
  | 'UWV_MELDING_W42'
  | 'EERSTEJAARSEVALUATIE_W52'
  | 'WIA_AANVRAAG_W87'
  | 'EINDDOSSIER_W91';

/** Kolom 5: de Poortwachter-Klok (verkeerslicht). */
export interface PoortwachterKlok {
  kleur: 'groen' | 'geel' | 'rood';
  mijlpaal_key: PoortwachterMijlpaalKey | null;
  label: string | null;
  datum: string | null;
  /** Positief = nog te gaan, negatief = over tijd. */
  dagen: number | null;
}

/** Kolom 7: status van één document in de Digital Signing Wrapper (metadata, nooit inhoud). */
export type DocumentStatus = 'concept' | 'ter_ondertekening' | 'werkgever_getekend' | 'ondertekend';
export type DocumentBron = 'professional' | 'werkgever_formulier' | 'werkgever_upload';
export interface PoortwachterDocumentMeta {
  id: string;
  dossier_id: string;
  soort: string;
  soort_label: string;
  titel: string;
  status: DocumentStatus;
  bron: DocumentBron;
  heeft_bestand: boolean;
  bestand_naam: string | null;
  document_hash: string | null;
  vrijgegeven_op: string | null;
  werkgever_ondertekend_op: string | null;
  sms_verzonden_op: string | null;
  ondertekend_op: string | null;
  zegel_hash: string | null;
  professional_naam: string;
}

export interface PoortwachterCase {
  id: string;
  case_code: string;
  pseudoniem: string | null;
  /** SHA-256(employer_id | contact) — de enige identifier die de database raakt. */
  medewerker_hash: string | null;
  lijnmanager: { user_id: string; naam: string } | null;
  ziekmelding_geverifieerd_op: string | null;
  vestiging: string | null;
  afdeling: string | null;
  ziekmelding_datum: string;
  dagen_verzuim: number;
  status: PoortwachterStatus;
  wvp_stap: WvpStap;
  klok: PoortwachterKlok;
  /** Loonsanctie-Risicometer in euro's (0 bij groen/geel). */
  risico_bedrag: number;
  hersteld_op: string | null;
  notitie: string | null;
  bedrijfsarts_dossier_id: string | null;
  arbeidsdeskundige_dossier_id: string | null;
  bedrijfsarts_dossier_status: string | null;
  arbeidsdeskundige_dossier_status: string | null;
  documenten: {
    bedrijfsarts: { totaal: number; ondertekend: number };
    arbeidsdeskundige: { totaal: number; ondertekend: number };
    lijst: PoortwachterDocumentMeta[];
  };
  week_nu: number;
  deadlines: PoortwachterDeadline[];
  dossier_export_vanaf_week: number;
}
export interface PoortwachterOverzicht {
  employer_id: string;
  beschikbaar: { bedrijfsarts: boolean; arbeidsdeskundige: boolean };
  uwv_portaal_url: string;
  risicometer: { fte: number | null; enterprise_grens: number; bedrag_mkb: number; bedrag_enterprise: number };
  cases: PoortwachterCase[];
}
export const getPoortwachterCases = () => api.get<{ data: PoortwachterOverzicht }>(`${BASE}/poortwachter`).then(d);
export const openPoortwachterCase = (body: {
  ziekmelding_datum: string;
  location_id?: string | null;
  department_id?: string | null;
  pseudoniem?: string | null;
  notitie?: string | null;
  medewerker_hash?: string | null;
  lijnmanager_user_id?: string | null;
}) => api.post<{ data: PoortwachterCase & { eerdere_cases: number } }>(`${BASE}/poortwachter`, body).then(d);

export interface Lijnmanager {
  user_id: string;
  naam: string;
  rol: string;
  afdeling: string | null;
}
export const getLijnmanagers = () => api.get<{ data: Lijnmanager[] }>(`${BASE}/poortwachter/lijnmanagers`).then(d);
export const verifieerZiekmelding = (caseId: string) => api.post(`${BASE}/poortwachter/${caseId}/verifieer`).then(d);

// ─── Menu 4 (DATA): Verzuim-Intelligence ─────────────────────────────────────

export interface VerzuimIntelligenceGroep {
  vestiging: string | null;
  afdeling: string | null;
  open: number;
  gesloten: number;
  groen: number;
  geel: number;
  rood: number;
  langdurig: number;
  risico_bedrag: number;
  gem_verzuimduur: number | null;
}
export interface VerzuimIntelligence {
  fte: number | null;
  totaal: { open: number; gesloten_12m: number; groen: number; geel: number; rood: number; langdurig: number; risico_bedrag: number; gem_verzuimduur: number | null; herhaald_verzuim: number };
  afdelingen: VerzuimIntelligenceGroep[];
  maanden: Array<{ maand: string; nieuw: number; hersteld: number; gem_duur_hersteld: number | null }>;
}
export const getVerzuimIntelligence = () => api.get<{ data: VerzuimIntelligence }>(`${BASE}/poortwachter/intelligence`).then(d);

// ─── Digital Signing Wrapper (werkgever-kant) ─────────────────────────────────

export interface DocumentVoorWerkgever extends PoortwachterDocumentMeta {
  inhoud: string;
  case_code: string;
  pseudoniem: string | null;
  akkoordverklaring: string;
}
export interface TekenResultaat {
  document: PoortwachterDocumentMeta;
  sms_verzonden: boolean;
  reden?: string;
}
export const getDocumentVoorWerkgever = (caseId: string, docId: string) =>
  api.get<{ data: DocumentVoorWerkgever }>(`${BASE}/poortwachter/${caseId}/documenten/${docId}`).then(d);
export const tekenDocumentAlsWerkgever = (caseId: string, docId: string) =>
  api.post<{ data: TekenResultaat }>(`${BASE}/poortwachter/${caseId}/documenten/${docId}/teken`, { akkoord: 'true' }).then(d);
export const uploadExternDocument = (caseId: string, body: { soort: string; titel: string; bestand: File }) => {
  const fd = new FormData();
  fd.append('soort', body.soort);
  fd.append('titel', body.titel);
  fd.append('akkoord', 'true');
  fd.append('bestand', body.bestand);
  return api.post<{ data: TekenResultaat }>(`${BASE}/poortwachter/${caseId}/documenten/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(d);
};
export const documentBestandUrl = (caseId: string, docId: string) => `${api.defaults.baseURL ?? ''}${BASE}/poortwachter/${caseId}/documenten/${docId}/bestand`;

const downloadBlob = async (url: string, bestandsnaam: string, fallback: string) => {
  try {
    const r = await api.get(url, { responseType: 'blob' });
    const href = URL.createObjectURL(r.data as Blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = bestandsnaam;
    a.click();
    URL.revokeObjectURL(href);
  } catch (err: any) {
    const blob = err?.response?.data;
    if (blob instanceof Blob) {
      const tekst = await blob.text();
      let message = fallback;
      try { message = JSON.parse(tekst).message || message; } catch { /* geen JSON */ }
      throw new Error(message);
    }
    throw err;
  }
};
/** Belagom Audit-Certificaat (PDF) van een 🟢 verzegeld document. */
export const downloadAuditCertificaat = (caseId: string, doc: PoortwachterDocumentMeta, caseCode: string) =>
  downloadBlob(`${BASE}/poortwachter/${caseId}/documenten/${doc.id}/certificaat.pdf`, `Audit-Certificaat_${caseCode}_${doc.soort}.pdf`, 'Certificaat ophalen mislukt');
/** De geüploade PDF (arbo-arts) als download. */
export const downloadDocumentBestand = (caseId: string, doc: PoortwachterDocumentMeta) =>
  downloadBlob(`${BASE}/poortwachter/${caseId}/documenten/${doc.id}/bestand`, doc.bestand_naam ?? `${doc.soort}.pdf`, 'Bestand ophalen mislukt');

// ─── Week 52: Eerstejaarsevaluatie-wizard ─────────────────────────────────────

export type DoelBehaald = 'ja' | 'deels' | 'nee';
export interface EerstejaarsPrefill {
  case_code: string;
  pseudoniem: string | null;
  werkgever: string | null;
  eerste_ziektedag: string;
  week_nu: number;
  deadline: string | null;
  pva_ondertekend_op: string | null;
  doel_opties: Record<DoelBehaald, string>;
  ingediend: { status: DocumentStatus; werkgever_ondertekend_op: string | null; ondertekend_op: string | null } | null;
  kan_indienen: boolean;
}
export interface EerstejaarsInvoer {
  doel_behaald: DoelBehaald;
  terugblik: string;
  reintegratievisie_medewerker: string;
  visie_werkgever: string;
  afspraken_tweede_jaar: Array<{ actie: string; wie: 'werkgever' | 'werknemer' | 'bedrijfsarts' | 'arbeidsdeskundige' | 'anders'; wanneer: string }>;
  spoor2_overwogen: boolean;
}
export const getEerstejaarsPrefill = (caseId: string) => api.get<{ data: EerstejaarsPrefill }>(`${BASE}/poortwachter/${caseId}/eerstejaarsevaluatie`).then(d);
export const dienEerstejaarsIn = (caseId: string, body: EerstejaarsInvoer) =>
  api.post<{ data: TekenResultaat }>(`${BASE}/poortwachter/${caseId}/eerstejaarsevaluatie`, body).then(d);

// ─── Stap 5: UWV-meldingsengine ──────────────────────────────────────────────

export type UwvMeldingSoort = '42e_week' | 'hersteld' | 'wia';
export type UwvKanaal = 'portaal' | 'koppeling';
export interface UwvMelding {
  id: string;
  soort: UwvMeldingSoort;
  soort_label: string;
  kanaal: UwvKanaal;
  status: 'VERZONDEN' | 'BEVESTIGD' | 'MISLUKT';
  uwv_referentie: string | null;
  payload_hash: string;
  verzonden_op: string | null;
  foutmelding: string | null;
}
export interface UwvVoorbereiding {
  soort: UwvMeldingSoort;
  soort_label: string;
  case: { id: string; case_code: string; pseudoniem: string | null; eerste_ziektedag: string; week_nu: number; vestiging: string | null; hersteld_op: string | null };
  werkgever: { bedrijfsnaam: string | null; kvk_nummer: string | null; loonheffingennummer: string | null; postcode: string | null; plaats: string | null };
  mijlpaal: { status: 'OPEN' | 'AFGEROND' | 'VERVALLEN'; deadline: string; dagen_te_gaan: number } | null;
  kanalen: { portaal: { beschikbaar: boolean; url: string }; koppeling: { beschikbaar: boolean; omschrijving: string } };
  historie: UwvMelding[];
  privacy_notitie: string;
}
export const getUwvVoorbereiding = (caseId: string, soort: UwvMeldingSoort = '42e_week') =>
  api.get<{ data: UwvVoorbereiding }>(`${BASE}/poortwachter/${caseId}/uwv-melding`, { params: { soort } }).then(d);
export const verzendUwvMelding = (caseId: string, body: { soort: UwvMeldingSoort; kanaal: UwvKanaal; uwv_referentie?: string | null; loonheffingennummer?: string | null; bevestigd?: boolean }) =>
  api.post<{ data: UwvMelding }>(`${BASE}/poortwachter/${caseId}/uwv-melding`, body).then(d);
export const downloadUwvBewijs = (caseId: string, m: UwvMelding, caseCode: string) =>
  downloadBlob(`${BASE}/poortwachter/${caseId}/uwv-melding/${m.id}/bewijs.pdf`, `UWV-melding_${caseCode}_${m.soort}.pdf`, 'Bewijs ophalen mislukt');

// ─── Week 87-91: dossier-audit ────────────────────────────────────────────────

export interface DossierAudit {
  compleet: boolean;
  week_nu: number;
  checks: Array<{ key: string; label: string; ok: boolean; detail: string | null }>;
}
export const getDossierAudit = (caseId: string) => api.get<{ data: DossierAudit }>(`${BASE}/poortwachter/${caseId}/dossier-audit`).then(d);
export const updatePoortwachterStatus = (id: string, body: { status: PoortwachterStatus; hersteld_op?: string | null; notitie?: string | null }) =>
  api.patch<{ data: PoortwachterCase }>(`${BASE}/poortwachter/${id}/status`, body).then(d);
export const boekPoortwachterProfessional = (id: string, rol: 'bedrijfsarts' | 'arbeidsdeskundige') =>
  api
    .post<{ data: { dossier_id: string; professional_id: string; booking_token: string; expires_at: string; url: string } }>(`${BASE}/poortwachter/${id}/boek`, { rol })
    .then(d);

export const rondPoortwachterMijlpaalAf = (
  caseId: string,
  mijlpaalId: string,
  body: { afronden?: boolean; checklist?: Array<{ key: string; gedaan: boolean }> }
) => api.patch(`${BASE}/poortwachter/${caseId}/mijlpalen/${mijlpaalId}`, body).then(d);

export type PvaDoel = 'eigen_werk' | 'aangepast_eigen_werk' | 'ander_werk_eigen_werkgever' | 'ander_werk_andere_werkgever';
export interface PvaPrefill {
  case_code: string;
  pseudoniem: string | null;
  werkgever: string | null;
  vestiging: string | null;
  afdeling: string | null;
  eerste_ziektedag: string;
  week_nu: number;
  bedrijfsarts: string | null;
  probleemanalyse_op: string | null;
  deadline: string | null;
  doelen: Record<PvaDoel, string>;
  ingediend: { status: DocumentStatus; vrijgegeven_op: string | null; werkgever_ondertekend_op: string | null; ondertekend_op: string | null; sms_verzonden_op: string | null } | null;
  kan_indienen: boolean;
}
export interface PvaInvoer {
  reintegratiedoel: PvaDoel;
  toelichting_doel: string;
  advies_bedrijfsarts: string;
  afspraken: Array<{ actie: string; wie: 'werkgever' | 'werknemer' | 'bedrijfsarts' | 'anders'; wanneer: string }>;
  casemanager: string;
  evaluatie_frequentie_weken: number;
}
export const getPvaPrefill = (caseId: string) => api.get<{ data: PvaPrefill }>(`${BASE}/poortwachter/${caseId}/plan-van-aanpak`).then(d);
export const dienPvaIn = (caseId: string, body: PvaInvoer) =>
  api.post<{ data: { id: string; status: DocumentStatus; sms_verzonden: boolean; reden?: string } }>(`${BASE}/poortwachter/${caseId}/plan-van-aanpak`, body).then(d);

/** Week 87: ZIP met alle SMS-ondertekende documenten; start direct de download. */
/** Download het versleutelde UWV-dossier; geeft het eenmalige ZIP-wachtwoord terug. */
export const downloadUwvDossier = async (caseId: string, caseCode: string): Promise<string | null> => {
  try {
    const r = await api.get(`${BASE}/poortwachter/${caseId}/uwv-dossier.zip`, { responseType: 'blob' });
    const wachtwoord = (r.headers['x-zip-wachtwoord'] as string | undefined) ?? null;
    const url = URL.createObjectURL(r.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `UWV-dossier_${caseCode}_${new Date().toISOString().slice(0, 10)}.zip`;
    a.click();
    URL.revokeObjectURL(url);
    return wachtwoord;
  } catch (err: any) {
    // Foutmelding zit bij responseType 'blob' in een Blob verpakt
    const blob = err?.response?.data;
    if (blob instanceof Blob) {
      const tekst = await blob.text();
      let message = 'Exporteren mislukt';
      try { message = JSON.parse(tekst).message || message; } catch { /* geen JSON */ }
      throw new Error(message);
    }
    throw err;
  }
};

// ─── Tabel 3: Interne B2B-dienstverlening ─────────────────────────────────────

export type B2bSoort =
  | 'meeting' | 'telefonisch_overleg' | 'overleg_op_locatie'
  | 'onboardingdag' | 'workshop' | 'casemanagement_overleg' | 'intervisie' | 'strategisch_overleg' | 'overig';
export const B2B_SOORT_LABEL: Record<B2bSoort, string> = {
  meeting: 'Meeting',
  telefonisch_overleg: 'Telefonisch overleg',
  overleg_op_locatie: 'Overleg op locatie',
  onboardingdag: 'Onboardingdag',
  workshop: 'Workshop',
  casemanagement_overleg: 'Strategisch casemanagement-overleg (HR)',
  intervisie: 'Intervisie',
  strategisch_overleg: 'Strategisch overleg',
  overig: 'Overig',
};
/** Wederzijdse handdruk (migratie 132): de andere partij dan `voorgesteld_door` bevestigt. */
export type B2bPartij = 'employer' | 'professional';
export type B2bBevestiging = 'WACHT' | 'BEVESTIGD' | 'GEWEIGERD';
export type B2bLocatie = 'online' | 'op_locatie' | 'telefonisch';
export type B2bStatus = 'GEPLAND' | 'UITGEVOERD' | 'GEFIATTEERD' | 'AFGEWEZEN' | 'GEFACTUREERD' | 'GEANNULEERD';

export interface B2bAfspraak {
  id: string;
  professional_id: string;
  professional_naam: string;
  professional_type: string | null;
  discipline_label: string;
  soort: B2bSoort;
  titel: string;
  omschrijving: string | null;
  start_tijd: string;
  eind_tijd: string;
  locatie_type: B2bLocatie;
  video_link: string | null;
  uren_gepland: number;
  uren_geschreven: number | null;
  uurtarief: number | null;
  bedrag: number | null;
  status: B2bStatus;
  submitted_at: string | null;
  approved_at: string | null;
  approved_ip: string | null;
  afwijzing_reden: string | null;
  voorgesteld_door: B2bPartij;
  bevestiging_status: B2bBevestiging;
  bevestigd_at: string | null;
  weigering_reden: string | null;
  payout_status: string | null;
  stripe_transfer_id: string | null;
}
export interface PoolProfessional {
  connection_id: number;
  professional_id: string;
  naam: string;
  professional_type: string;
  discipline_label: string;
  uurtarief: number | null;
}
export interface B2bOverzicht {
  regels: B2bAfspraak[];
  totaal: { te_fiatteren: number; te_bevestigen: number; gepland: number; gefiatteerd_bedrag: number };
  pool: PoolProfessional[];
}
export const getB2bAfspraken = () => api.get<{ data: B2bOverzicht }>(`${BASE}/b2b`).then(d);
export const planB2bAfspraak = (body: {
  professional_id: string;
  soort: B2bSoort;
  titel: string;
  omschrijving?: string | null;
  start_tijd: string;
  eind_tijd: string;
  locatie_type: B2bLocatie;
}) => api.post<{ data: B2bAfspraak }>(`${BASE}/b2b`, body).then(d);
export const fiatteerB2bAfspraak = (id: string) => api.post<{ data: B2bAfspraak }>(`${BASE}/b2b/${id}/fiat`).then(d);
export const wijsB2bAfspraakAf = (id: string, reden: string) => api.post(`${BASE}/b2b/${id}/afwijzen`, { reden });
export const annuleerB2bAfspraak = (id: string) => api.post(`${BASE}/b2b/${id}/annuleren`);
export const bevestigB2bAfspraak = (id: string) => api.post(`${BASE}/b2b/${id}/bevestigen`);
export const weigerB2bAfspraak = (id: string, reden?: string) => api.post(`${BASE}/b2b/${id}/weigeren`, { reden: reden || null });

// ─── Tabel 3: B2B projectregie-grid — Long-term Project Engine & Budget-Grendel (136) ──

export type B2bMilestoneStatus =
  | 'LOCKED_PENDING'
  | 'ACTIVE_RUNNING'
  | 'CAP_REACHED_PENDING_APPROVAL'
  | 'COMPLETED_WAITING_FOR_FIAT'
  | 'ARCHIVED';
export type BudgetSignaal = 'CAP_BEREIKT' | 'WACHT_OP_FIAT' | 'BIJNA_CAP' | 'GEEN_UREN' | null;

export interface ProjectMijlpaal {
  id: string;
  volgnummer: number;
  titel: string;
  status: B2bMilestoneStatus;
  cap_uren: number | null;
  oorspronkelijke_cap_uren: number | null;
  extra_uren_toegekend: number;
  geschreven_uren: number;
  resterende_uren: number | null;
  percentage: number;
  uurtarief: number | null;
  budget_bedrag: number;
  verbruik_bedrag: number | null;
  gefactureerd_bedrag: number | null;
  geactiveerd_op: string | null;
  cap_bereikt_op: string | null;
  afgerond_op: string | null;
  approved_at: string | null;
  approved_ip: string | null;
  invoice_id: string | null;
}
export interface B2bProject {
  id: string;
  project_code: string;
  titel: string;
  omschrijving: string | null;
  status: 'ACTIEF' | 'AFGEROND' | 'GEANNULEERD';
  start_datum: string;
  verwachte_eind_datum: string | null;
  geactiveerd_op: string | null;
  professional: { id: string; naam: string; professional_type: string; discipline_label: string; uurtarief: number | null };
  actieve_mijlpaal: ProjectMijlpaal | null;
  signaal: BudgetSignaal;
  mijlpalen: ProjectMijlpaal[];
  totaal: { mijlpalen: number; afgerond: number; cap_uren: number; geschreven_uren: number; budget_bedrag: number; verbruik_bedrag: number; gefactureerd_bedrag: number };
}
export interface B2bProjectenOverzicht {
  regels: B2bProject[];
  totaal: { projecten: number; actief: number; cap_bereikt: number; wacht_op_fiat: number; verbruik_bedrag: number };
  pool: PoolProfessional[];
}
export interface WizardMijlpaalInvoer {
  titel: string;
  cap_uren: number;
}
export interface UrenLog {
  id: string;
  datum: string;
  uren: number;
  omschrijving: string;
  uurtarief: number | null;
  bedrag: number | null;
  geregistreerd_op: string;
  bevroren: boolean;
}
export interface MijlpaalUrenLogs {
  mijlpaal: ProjectMijlpaal & { project_titel: string; project_code: string };
  logs: UrenLog[];
}
export interface AccordeerResultaat {
  mijlpaal: ProjectMijlpaal;
  volgende: ProjectMijlpaal | null;
  project_afgerond: boolean;
  split: { bruto: number; professional: number; platform_fee: number; fee_percentage: number };
  /** Direct aangemaakte PROJECT_MILESTONE-verzamelfactuur (null als de maandcron het overneemt). */
  factuur: { invoiceId: string; invoiceNumber: string; nettoOmzet: number; platformFee: number; totaalIncBtw: number } | null;
}

export const getB2bProjecten = () => api.get<{ data: B2bProjectenOverzicht }>(`${BASE}/projecten`).then(d);
export const maakB2bProject = (body: {
  professional_id: string;
  titel: string;
  omschrijving?: string | null;
  start_datum: string;
  verwachte_eind_datum?: string | null;
  mijlpalen: WizardMijlpaalInvoer[];
}) => api.post<{ data: B2bProject }>(`${BASE}/projecten`, body).then(d);
export const getMijlpaalUrenLogs = (milestoneId: string) =>
  api.get<{ data: MijlpaalUrenLogs }>(`${BASE}/projecten/mijlpalen/${milestoneId}/uren`).then(d);
export const keurExtraUrenGoed = (milestoneId: string, extraUren: number) =>
  api.post<{ data: ProjectMijlpaal }>(`${BASE}/projecten/mijlpalen/${milestoneId}/extra-uren`, { extra_uren: extraUren }).then(d);
export const accordeerMijlpaal = (milestoneId: string) =>
  api.post<{ data: AccordeerResultaat }>(`${BASE}/projecten/mijlpalen/${milestoneId}/accordeer`).then(d);

// Professional-kant (/api/professional/b2b)
const PROF = '/professional/b2b';
export interface ProfessionalB2bAfspraak extends B2bAfspraak {
  organisatie: string;
}
export interface B2bWerkgever {
  employer_id: string;
  organisatie: string;
}
export const getProfessionalB2bAfspraken = () => api.get<{ data: ProfessionalB2bAfspraak[] }>(PROF).then(d);
export const getB2bWerkgevers = () => api.get<{ data: B2bWerkgever[] }>(`${PROF}/werkgevers`).then(d);
export const planB2bAfspraakAlsProfessional = (body: {
  employer_id: string;
  soort: B2bSoort;
  titel: string;
  omschrijving?: string | null;
  start_tijd: string;
  eind_tijd: string;
  locatie_type: B2bLocatie;
  /** Actie-pad B (migratie 192): geautoriseerde contactpersoon bij de organisatie */
  contactpersoon_user_id?: string | null;
}) => api.post(PROF, body);
export const bevestigB2bAlsProfessional = (id: string) => api.post(`${PROF}/${id}/bevestigen`);
export const weigerB2bAlsProfessional = (id: string, reden?: string) => api.post(`${PROF}/${id}/weigeren`, { reden: reden || null });
export const trekB2bIn = (id: string) => api.post(`${PROF}/${id}/intrekken`);
export const dienB2bUrenIn = (id: string, uren: number) => api.post(`${PROF}/${id}/indienen`, { uren_geschreven: uren });

// ─── Tabel 4: Calamiteiten & crisis-claims ────────────────────────────────────

export interface GarantiefondsStatus {
  module_actief: boolean;
  jaar: number;
  dekking_pct: number;
  on_demand_uurtarief: number;
  no_claim_cap: number;
  vrije_uitrukken: number;
  gebruikte_uitrukken: number;
  resterend: number;
  abonnementen: number;
  fonds_saldo: number;
}
export interface CrisisDagdeel {
  shift: 'dag' | 'avond' | 'nacht';
  weekend: boolean;
  label: string;
  factor: number;
}
export interface CrisisResponder {
  id: string;
  professional_id: string;
  naam: string;
  professional_type: string | null;
  discipline_label: string;
  status: string;
  volgnummer: number | null;
  geaccepteerd_op: string | null;
  uren: number | null;
  uren_gefactureerd: number;
  cap_toegepast: boolean;
  uren_definitief: boolean;
  reis_km: number | null;
  reis_overloop: boolean;
  reiskosten: number;
  bedrag_bruto: number;
  gedekt_door: 'FONDS' | 'FACTUUR';
  uitbetaling_status: string | null;
}
export type CrisisFactuurStatus = 'GEEN' | 'PROGNOSE' | 'WACHT_OP_VERZAMELFACTUUR' | 'OP_VERZAMELFACTUUR';
/** Eén rij van het 8-koloms grid (Calamiteiten & Crisis-Claims). */
export interface CrisisClaim {
  id: string;
  // Kolom 1 — Incident-ID & locatie
  incident_nr: number | null;
  incident_label: string;
  vestiging: string | null;
  vestiging_plaats: string | null;
  omschrijving: string | null;
  details_vergrendeld: boolean;
  // Kolom 2 — Inzet-omvang
  inzet_type: 'SINGLE' | 'DUAL_RESPONSE';
  aantal_gewenst: number;
  punten_afgeboekt: number;
  // Kolom 3 — Tijdas
  incident_datum: string;
  dagdeel: CrisisDagdeel;
  // Kolom 4 — Responders
  responders: CrisisResponder[];
  status: 'OPEN' | 'ACCEPTED' | 'CLOSED' | 'CANCELLED';
  video_url: string | null;
  geaccepteerd_op: string | null;
  gesloten_op: string | null;
  // Kolom 5 — Inzetduur
  uren_gewerkt: number;
  uren_gefactureerd: number;
  uren_minimaal: number;
  cap_toegepast: boolean;
  uren_definitief: boolean;
  min_uren_pp: number;
  // Kolom 6 — Regio & reiskosten
  regio_code: string;
  regio_naam: string;
  escalatie_niveau: number;
  escalatie_label: string | null;
  reis_overloop: boolean;
  reis_km_totaal: number;
  reiskosten: number;
  // Kolom 7 — Garantiefonds-meter
  dekking: { type: 'VOLLEDIG' | 'GEDEELTELIJK' | 'GEEN'; gewenst: number; gedekt: number; gefactureerd: number };
  dekking_pct: number;
  tegoed_resterend: number | null;
  dekking_label: string;
  // Kolom 8 — Factuur-plint
  uurtarief: number;
  kosten_totaal: number;
  factuur_bedrag: number;
  factuur_status: CrisisFactuurStatus;
  factuur_nummer: string | null;
  factuur_invoice_status: string | null;
}
export interface CrisisOverzicht {
  fonds: GarantiefondsStatus;
  claims: CrisisClaim[];
}
export const getCrisisClaims = () => api.get<{ data: CrisisOverzicht }>(`${BASE}/calamiteiten`).then(d);
